import type { ReactNode } from 'react';
import { ChevronRight, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { LoadingState, EmptyState, ErrorState } from '@/components/ui/states';
import { ScopeLock } from '@/components/ui/scope-lock';

interface Row {
  id: string;
  title: string;
  subtitle?: string;
  meta?: string;
  /** `warning` marks a row that still needs work, e.g. tables missing from the map. */
  metaTone?: 'muted' | 'warning' | 'success';
  muted?: boolean;
  /**
   * §24.4 — why editing *this* row is refused, when it is. The floor, area and
   * table writes all declare `@RequireScope(...)` on the row's own id, but the
   * list routes do not, so a scoped manager is handed rows they cannot touch.
   * Set, the Edit button greys out and says which.
   */
  lockedReason?: string | null;
}

interface Props {
  title: string;
  /** Shown instead of the list when the previous step has not been chosen yet. */
  waitingFor?: string;
  rows: Row[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onCreate: () => void;
  onEdit: (id: string) => void;
  createLabel: string;
  emptyTitle: string;
  emptyHint: string;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  canCreate: boolean;
  canEdit: boolean;
  /**
   * §24.4 — why creating here is refused. Create is scoped to the *parent*
   * (`POST /floors` → `@RequireScope(BRANCH,'branchId')`, `/areas` → FLOOR,
   * `/tables` → AREA), so this is one reason for the column, not per row.
   */
  createBlockedReason?: string | null;
  footer?: ReactNode;
}

/**
 * One rung of the spaces hierarchy: branch › floor › area › table.
 *
 * Each column is disabled until its parent is chosen, because the API requires
 * the parent id on create — showing an enabled "Add area" button with no floor
 * selected would only produce a 400.
 */
export function SpaceColumn({
  title,
  waitingFor,
  rows,
  selectedId,
  onSelect,
  onCreate,
  onEdit,
  createLabel,
  emptyTitle,
  emptyHint,
  loading,
  error,
  onRetry,
  canCreate,
  canEdit,
  createBlockedReason,
  footer,
}: Props) {
  const { t } = useTranslation();
  const createLocked = Boolean(createBlockedReason);

  return (
    <section className="flex min-h-[26rem] flex-1 flex-col rounded-xl border bg-surface">
      <header className="flex items-center justify-between gap-2 border-b px-4 py-3">
        <div className="flex items-baseline gap-2">
          <h3 className="text-sm font-semibold">{title}</h3>
          {!waitingFor && !loading && (
            <span className="text-xs text-ink-muted">{rows.length}</span>
          )}
        </div>
        {canCreate && !waitingFor && (
          <ScopeLock locked={createLocked} reason={createBlockedReason ?? ''}>
            <Button size="sm" variant="secondary" onClick={onCreate} disabled={createLocked}>
              <Plus className="h-4 w-4" />
              {createLabel}
            </Button>
          </ScopeLock>
        )}
      </header>

      <div className="scrollbar-thin flex-1 overflow-y-auto">
        {waitingFor ? (
          <p className="px-4 py-10 text-center text-sm text-ink-muted">
            {waitingFor}
          </p>
        ) : loading ? (
          <LoadingState />
        ) : error ? (
          <ErrorState message={error} onRetry={onRetry} />
        ) : rows.length === 0 ? (
          <EmptyState
            title={emptyTitle}
            hint={emptyHint}
            action={
              canCreate ? (
                <ScopeLock locked={createLocked} reason={createBlockedReason ?? ''}>
                  <Button size="sm" onClick={onCreate} disabled={createLocked}>
                    {createLabel}
                  </Button>
                </ScopeLock>
              ) : undefined
            }
          />
        ) : (
          <ul className="divide-y">
            {rows.map((row) => (
              <li key={row.id}>
                <div
                  className={cn(
                    'flex items-center gap-2 px-4 py-2.5',
                    row.id === selectedId && 'bg-accent-soft',
                    row.muted && 'opacity-55',
                  )}
                >
                  <button
                    onClick={() => onSelect(row.id)}
                    className="flex min-w-0 flex-1 items-center gap-2 text-start"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">{row.title}</span>
                      {row.subtitle && (
                        <span className="block truncate text-xs text-ink-muted">
                          {row.subtitle}
                        </span>
                      )}
                    </span>
                    {row.meta && (
                      <span
                        className={cn(
                          'shrink-0 text-xs',
                          row.metaTone === 'warning'
                            ? 'font-medium text-amber-700 dark:text-amber-400'
                            : row.metaTone === 'success'
                              ? 'text-green-700 dark:text-green-400'
                              : 'text-ink-muted',
                        )}
                      >
                        {row.meta}
                      </span>
                    )}
                    <ChevronRight className="h-4 w-4 shrink-0 text-line-strong rtl:rotate-180" />
                  </button>

                  {canEdit && (
                    <ScopeLock locked={Boolean(row.lockedReason)} reason={row.lockedReason ?? ''}>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={Boolean(row.lockedReason)}
                        onClick={() => onEdit(row.id)}
                      >
                        {t('setup.edit')}
                      </Button>
                    </ScopeLock>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {footer && <div className="border-t px-4 py-2">{footer}</div>}
    </section>
  );
}
